import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { clearAuth } from '../utils/api';
import { clearFixModeState } from '../utils/fixModeAdvanceSignal';

/**
 * User Store
 * 
 * IMPORTANT: isAuthenticated is DERIVED from user presence, not stored separately.
 * This prevents state mismatches where isAuthenticated=true but user=null.
 */
const useUserStore = create(
  persist(
    (set, get) => ({
      // Core state - user is the source of truth for auth
      user: null,
      profile: null,
      
      // Derived getter - do not store isAuthenticated separately!
      // Access via: useUserStore.getState().isAuthenticated or useUserStore(state => !!state.user)

      // Onboarding data (temporary, stored before auth)
      onboardingData: null,

      // Set user (also updates isAuthenticated implicitly)
      setUser: (user) => {
        console.log('[Store] setUser:', user ? { userId: user.user_id, onboardingComplete: user.onboarding_complete } : null);
        set({ user });
      },
      
      setProfile: (profile) => set({ profile }),
      
      // Store onboarding data temporarily
      setOnboardingData: (data) => set((state) => ({
        onboardingData: { ...state.onboardingData, ...data }
      })),
      
      clearOnboardingData: () => set({ onboardingData: null }),

      // Logout - clears everything
      logout: () => {
        console.log('[Store] logout called');
        clearAuth();
        // Fix Days session state lives in sessionStorage, keyed by tab not by
        // account — without this, a stale Fix Mode banner/queue from this
        // session could leak into whichever account next logs in on this
        // same tab (see AGENTS.md pre-launch audit, finding FIXDAYS-3).
        clearFixModeState();
        set({
          user: null,
          profile: null,
          onboardingData: null
        });
      },
    }),
    {
      name: 'fitxess-user-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user and profile
      partialize: (state) => ({
        user: state.user,
        profile: state.profile,
      }),
    }
  )
);

// Helper selector for isAuthenticated (derived from user)
export const selectIsAuthenticated = (state) => !!state.user;

export default useUserStore;
